import 'antlr4';
import antlr4 from 'antlr4';
import { lazy } from 'src/app/shared/utils';
import { default as QueryExprLexer } from './grammar/QueryExprLexer';
import { default as QueryExprParser } from './grammar/QueryExprParser';
import { default as QueryExprVisitor } from './grammar/QueryExprVisitor';
import { BinaryOperator, BinarySyntax, FunctionCallSyntax, IdentifierSyntax, IndexSyntax, ListSyntax, LiteralSyntax, MapSyntax, MapSyntaxItem, MemberSyntax, SYNTAX_NODE_SYMBOL, SyntaxNode, SyntaxSpan, TextLocation, UnaryOperator, UnarySyntax } from './Syntax';
import { UTC_TIMEZONE, createDateTimeValue, createDateValue, createTimeValue } from './Types';
import { LiteralType, QueryExprSyntax } from 'src/app/services/api.services';
import { logger } from '../../utils/logger';
import { StringEscape } from '../../../shared/utils/stringEscape';

const LOGGER = logger('DataQueryParser');

export class ParseError extends Error {
  constructor(public readonly location: TextLocation, message: string) {
    super(message);
  }
}

const parsedExpressionCache = new Map<string, SyntaxNode>();

export function parse(expression: string | QueryExprSyntax, options?: { errors?: ParseError[], throwOnError?: boolean }): SyntaxNode {
  if (expression instanceof SyntaxNode) {
    LOGGER.warn(`SyntaxNode passed to parse`, expression);
    return expression;
  }

  let { errors, throwOnError } = options ?? {};

  if (!throwOnError)
    errors ??= [];

  try {
    if (typeof expression === 'object') {
      return SyntaxNode.from(expression);
    }
    else {
      let node = parsedExpressionCache.get(expression);
      if (node)
        return node;

      let inputStream = new antlr4.InputStream(expression);
      let lexer = new QueryExprLexer(inputStream);
      let tokens = new antlr4.CommonTokenStream(lexer);
      let parser = new QueryExprParser(tokens);

      parser.buildParseTrees = true;

      if (errors) {
        let errorListener = new ErrorListener(errors);

        lexer.addErrorListener(errorListener);
        parser.addErrorListener(errorListener);
      }

      let tree = parser.rootExpression();

      node = <SyntaxNode>(tree as any)?.accept(new AntlrParseTreeVisitor(expression));
      parsedExpressionCache.set(expression, node);
      return node;
    }
  }
  catch (err) {
    LOGGER.error(`An unhandled error occurred when parsing the `, {
      expression,
      error: err
    });

    if (throwOnError !== false)
      throw err;
  }
  finally {
    if (errors?.length) {
      if (throwOnError !== false) {
        let errorsString = errors.join('\r\n');
        throw new Error(errorsString);
      }

      LOGGER.warn(`One or more parsing errors reported when parsing the QueryExpr`, {
        expression,
        errors
      });
    }
  }
}

class ErrorListener extends antlr4.error.ErrorListener {
  constructor(private errors: ParseError[]) {
    super();
  }

  syntaxError(
    recognizer: any,
    offendingSymbol: antlr4.Token,
    line: number,
    column: number,
    msg: string,
    e: antlr4.error.RecognitionException,
  ) {
    let size = offendingSymbol ? (offendingSymbol.stop - offendingSymbol.start + 1) : 1;
    let endColumn = column + size;

    if (msg.indexOf("missing ')'") !== -1) {
      this.errors.push(
        new ParseError(
          new TextLocation(line, column, line, endColumn), `Expected a closing parenthesis.`));
    }
    else if (msg.indexOf("missing ']'") !== -1) {
      this.errors.push(
        new ParseError(
          new TextLocation(line, column, line, endColumn), `Expected a closing bracket.`));
    }
    else {
      if (e instanceof antlr4.error.InputMismatchException) {
        let message = offendingSymbol.type === antlr4.Token.EOF
          ? `Unexpected end of expression.`
          : `Unexpected syntax '${offendingSymbol.text}'`;

        this.errors.push(
          new ParseError(
            new TextLocation(line, column, line, endColumn), message));
      }
      else if ((msg.indexOf("missing '") !== -1)
        || (msg.indexOf("mismatched input") !== -1)
        || (msg.indexOf("extraneous input") !== -1)
        || (msg.indexOf("token recognition error") !== -1)) {
        this.errors.push(
          new ParseError(
            new TextLocation(line, column, line, endColumn), offendingSymbol ? `Unexpected syntax '${offendingSymbol.text}'.` : `Unexpected syntax.`));
      }
    }
  }
}

class AntlrParseTreeVisitor extends QueryExprVisitor {
  constructor(private readonly expression: string) {
    super();
  }

  private _sourceLines: string[];
  private get sourceLines() { return this._sourceLines ??= this.expression.split('\n'); }


  private createSpan(ctx: antlr4.ParserRuleContext): SyntaxSpan;
  private createSpan(start: antlr4.Token, stop: antlr4.Token): SyntaxSpan;
  private createSpan(startOrCtx: antlr4.Token | antlr4.ParserRuleContext, stop?: antlr4.Token): SyntaxSpan {
    let start: antlr4.Token;

    if (stop === undefined) {
      start = (startOrCtx.start === -1) ? null : <antlr4.Token>startOrCtx.start;
      stop = (startOrCtx.stop === -1) ? null : <antlr4.Token>startOrCtx.stop;
    }
    else {
      start = <antlr4.Token>startOrCtx;
    }

    let startLine = start.line;
    let startColumn = start.column + 1;

    let endLine = startLine;
    let endColumn = startColumn;

    if (stop) {
      endLine = stop.line;
      endColumn = stop.column + 1;
    }
    else
      stop = start;

    endColumn += stop.stop - stop.start;

    return new SyntaxSpan(this.sourceLines, startLine, startColumn, endLine, endColumn);
  }

  visitRootExpression(ctx: QueryExprParser.RootExpressionContext) {
    return ctx.expression().accept(this);
  }

  VisitParenthesizedExpr(ctx: QueryExprParser.ParenthesizedExprContext) {
    return ctx.expression().accept(this);
  }


  visitUnaryPlusExpr(ctx: QueryExprParser.UnaryPlusExprContext) {
    return new UnarySyntax(
      this.createSpan(ctx),
      UnaryOperator.Plus,
      <SyntaxNode>ctx.Right.accept(this)
    );
  }

  visitUnaryMinusExpr(ctx: QueryExprParser.UnaryMinusExprContext) {
    return new UnarySyntax(
      this.createSpan(ctx),
      UnaryOperator.Minus,
      <SyntaxNode>ctx.Right.accept(this)
    );
  }

  visitUnaryNotExpr(ctx: QueryExprParser.UnaryNotExprContext) {
    return new UnarySyntax(
      this.createSpan(ctx),
      UnaryOperator.Not,
      <SyntaxNode>ctx.Right.accept(this)
    );
  }


  visitRaiseToExpr(ctx: QueryExprParser.RaiseToExprContext) {
    return new BinarySyntax(
      this.createSpan(ctx),
      BinaryOperator.RaiseTo,
      <SyntaxNode>ctx.Left.accept(this),
      <SyntaxNode>ctx.Right.accept(this)
    );
  }

  visitMultiplyExpr(ctx: QueryExprParser.MultiplyExprContext) {
    return new BinarySyntax(
      this.createSpan(ctx),
      BinaryOperator.Multiply,
      <SyntaxNode>ctx.Left.accept(this),
      <SyntaxNode>ctx.Right.accept(this)
    );
  }

  visitDivideExpr(ctx: QueryExprParser.DivideExprContext) {
    return new BinarySyntax(
      this.createSpan(ctx),
      BinaryOperator.Divide,
      <SyntaxNode>ctx.Left.accept(this),
      <SyntaxNode>ctx.Right.accept(this)
    );
  }

  visitModExpr(ctx: QueryExprParser.ModExprContext) {
    return new BinarySyntax(
      this.createSpan(ctx),
      BinaryOperator.Mod,
      <SyntaxNode>ctx.Left.accept(this),
      <SyntaxNode>ctx.Right.accept(this)
    );
  }

  visitAddExpr(ctx: QueryExprParser.AddExprContext) {
    return new BinarySyntax(
      this.createSpan(ctx),
      BinaryOperator.Add,
      <SyntaxNode>ctx.Left.accept(this),
      <SyntaxNode>ctx.Right.accept(this)
    );
  }

  visitSubtractExpr(ctx: QueryExprParser.SubtractExprContext) {
    return new BinarySyntax(
      this.createSpan(ctx),
      BinaryOperator.Subtract,
      <SyntaxNode>ctx.Left.accept(this),
      <SyntaxNode>ctx.Right.accept(this)
    );
  }


  visitLessThanExpr(ctx: QueryExprParser.LessThanExprContext) {
    return new BinarySyntax(
      this.createSpan(ctx),
      BinaryOperator.LessThan,
      <SyntaxNode>ctx.Left.accept(this),
      <SyntaxNode>ctx.Right.accept(this)
    );
  }

  visitGreaterThanExpr(ctx: QueryExprParser.GreaterThanExprContext) {
    return new BinarySyntax(
      this.createSpan(ctx),
      BinaryOperator.GreaterThan,
      <SyntaxNode>ctx.Left.accept(this),
      <SyntaxNode>ctx.Right.accept(this)
    );
  }

  visitLessThanOrEqualExpr(ctx: QueryExprParser.LessThanOrEqualExprContext) {
    return new BinarySyntax(
      this.createSpan(ctx),
      BinaryOperator.LessThanOrEqual,
      <SyntaxNode>ctx.Left.accept(this),
      <SyntaxNode>ctx.Right.accept(this)
    );
  }

  visitGreaterThanOrEqualExpr(ctx: QueryExprParser.GreaterThanOrEqualExprContext) {
    return new BinarySyntax(
      this.createSpan(ctx),
      BinaryOperator.GreaterThanOrEqual,
      <SyntaxNode>ctx.Left.accept(this),
      <SyntaxNode>ctx.Right.accept(this)
    );
  }


  visitEqualExpr(ctx: QueryExprParser.EqualExprContext) {
    return new BinarySyntax(
      this.createSpan(ctx),
      BinaryOperator.Equal,
      <SyntaxNode>ctx.Left.accept(this),
      <SyntaxNode>ctx.Right.accept(this)
    );
  }

  visitNotEqualExpr(ctx: QueryExprParser.NotEqualExprContext) {
    return new BinarySyntax(
      this.createSpan(ctx),
      BinaryOperator.NotEqual,
      <SyntaxNode>ctx.Left.accept(this),
      <SyntaxNode>ctx.Right.accept(this)
    );
  }


  visitAndExpr(ctx: QueryExprParser.AndExprContext) {
    return new BinarySyntax(
      this.createSpan(ctx),
      BinaryOperator.And,
      <SyntaxNode>ctx.Left.accept(this),
      <SyntaxNode>ctx.Right.accept(this)
    );
  }

  visitOrExpr(ctx: QueryExprParser.OrExprContext) {
    return new BinarySyntax(
      this.createSpan(ctx),
      BinaryOperator.Or,
      <SyntaxNode>ctx.Left.accept(this),
      <SyntaxNode>ctx.Right.accept(this)
    );
  }


  visitConcatExpr(ctx: QueryExprParser.ConcatExprContext) {
    return new BinarySyntax(
      this.createSpan(ctx),
      BinaryOperator.Concat,
      <SyntaxNode>ctx.Left.accept(this),
      <SyntaxNode>ctx.Right.accept(this)
    );
  }


  visitWithExpr(ctx: QueryExprParser.WithExprContext) {
    return new BinarySyntax(
      this.createSpan(ctx),
      BinaryOperator.With,
      <SyntaxNode>ctx.Left.accept(this),
      <SyntaxNode>ctx.Right.accept(this)
    );
  }

  visitWithoutExpr(ctx: QueryExprParser.WithoutExprContext) {
    return new BinarySyntax(
      this.createSpan(ctx),
      BinaryOperator.Without,
      <SyntaxNode>ctx.Left.accept(this),
      <SyntaxNode>ctx.Right.accept(this)
    );
  }

  visitInExpr(ctx: QueryExprParser.InExprContext) {
    return new BinarySyntax(
      this.createSpan(ctx),
      BinaryOperator.In,
      <SyntaxNode>ctx.Left.accept(this),
      <SyntaxNode>ctx.Right.accept(this)
    );
  }


  visitIndex(ctx: QueryExprParser.IndexExprContext) {
    return new IndexSyntax(
      this.createSpan(ctx),
      <SyntaxNode>ctx.Subject.accept(this),
      <SyntaxNode>ctx.Key.accept(this)
    );
  }

  visitIndexExpr(ctx: QueryExprParser.IndexExprContext) {
    return new IndexSyntax(
      this.createSpan(ctx),
      <SyntaxNode>ctx.Subject.accept(this),
      <SyntaxNode>ctx.Key.accept(this)
    );
  }

  visitMember(ctx: QueryExprParser.MemberExprContext) {
    return new MemberSyntax(
      this.createSpan(ctx),
      <SyntaxNode>ctx.Subject.accept(this),
      (ctx.Key.accept(this) as IdentifierSyntax).value
    );
  }

  visitMemberExpr(ctx: QueryExprParser.MemberExprContext) {
    return new MemberSyntax(
      this.createSpan(ctx),
      <SyntaxNode>ctx.Subject.accept(this),
      (ctx.Key.accept(this) as IdentifierSyntax).value
    );
  }

  visitFunctionCall(ctx: QueryExprParser.FunctionCallContext) {
    let functionName = <string>(ctx.UNESCAPED_IDENTIFIER() as any).getText();

    if (!ctx.LPAREN()) {
      throw new ParseError(this.createSpan(ctx), `Expected an opening parenthesis following ${functionName}.`);
    }

    if (!ctx.RPAREN()) {
      throw new ParseError(this.createSpan(ctx), `Expected a closing parenthesis.`);
    }

    let args: SyntaxNode[] = [];

    let callArgs = ctx.callArgs()?.values();
    if (callArgs) {
      args = (callArgs.expression as any)().map(a => this.visit(a));
    }

    return new FunctionCallSyntax(this.createSpan(ctx), functionName, args);
  }

  visitIdentifier(ctx: QueryExprParser.IdentifierContext) {
    let escaped = ctx.ESCAPED_IDENTIFIER();
    if (escaped) {
      let text = (escaped as any).getText() as string;
      let identifierText = text.substring(1, text.length - 1).trim();
      return new IdentifierSyntax(this.createSpan(ctx), identifierText);
    }
    else {
      let text = (ctx.UNESCAPED_IDENTIFIER() as any).getText() as string;
      return new IdentifierSyntax(this.createSpan(ctx), text);
    }
  }


  visitNullLiteral(ctx: QueryExprParser.NullLiteralContext) {
    return new LiteralSyntax(this.createSpan(ctx), LiteralType.Null, null);
  }

  visitTrueLiteral(ctx: QueryExprParser.TrueLiteralContext) {
    return new LiteralSyntax(this.createSpan(ctx), LiteralType.Boolean, true);
  }

  visitFalseLiteral(ctx: QueryExprParser.FalseLiteralContext) {
    return new LiteralSyntax(this.createSpan(ctx), LiteralType.Boolean, false);
  }

  visitDateLiteral(ctx: QueryExprParser.DateLiteralContext) {
    const PREFIX = 'date`';
    let value = ctx.getText();
    let text = ctx.getText().substring(PREFIX.length, value.length - 1);
    let dateTimeValue = createDateValue(text);
    return new LiteralSyntax(this.createSpan(ctx), LiteralType.Date, text);
  }
  
  visitTimeLiteral(ctx: QueryExprParser.TimeLiteralContext) {
    const PREFIX = 'time`';
    let value = ctx.getText();
    let text = ctx.getText().substring(PREFIX.length, value.length - 1);
    let dateTimeValue = createTimeValue(text);
    return new LiteralSyntax(this.createSpan(ctx), LiteralType.Time, text);
  }

  visitDateTimeLiteral(ctx: QueryExprParser.DateTimeLiteralContext) {
    const PREFIX = 'datetime`';
    let value = ctx.getText();
    let text = ctx.getText().substring(PREFIX.length, value.length - 1);
    let dateTimeValue = createDateTimeValue(text, UTC_TIMEZONE);
    return new LiteralSyntax(this.createSpan(ctx), LiteralType.DateTime, text);
  }

  visitIntegerLiteral(ctx: QueryExprParser.IntegerLiteralContext) {
    let text = ctx.getText();
    let value = Number(text);

    if (isNaN(value))
      throw new ParseError(this.createSpan(ctx), `Invalid integer`);

    return new LiteralSyntax(this.createSpan(ctx), LiteralType.Integer, value);
  }

  visitNumberLiteral(ctx: QueryExprParser.NumberLiteralContext) {
    let text = ctx.getText();
    let value = Number(text);

    if (isNaN(value))
      throw new ParseError(this.createSpan(ctx), `Invalid number`);

    return new LiteralSyntax(this.createSpan(ctx), LiteralType.Number, value);
  }

  visitStringLiteral(ctx: QueryExprParser.StringLiteralContext) {
    let value = ctx.getText();

    let escapedText = ctx.getText().substring(1, value.length - 1);

    let text = StringEscape.unescape(escapedText);

    return new LiteralSyntax(this.createSpan(ctx), LiteralType.String, text);
  }


  visitFunctionCallExpr(ctx: QueryExprParser.FunctionCallExprContext) {
    return ctx.functionCall().accept(this);
  }

  visitIdentifierExpr(ctx: QueryExprParser.IdentifierExprContext) {
    return ctx.identifier().accept(this);
  }

  visitLiteralExpr(ctx: QueryExprParser.LiteralContext) {
    return this.visitChildren(ctx);
  }


  visitList(ctx: QueryExprParser.ListContext) {
    if (!ctx.LBRACE()) {
      throw new ParseError(this.createSpan(ctx), `Expected an opening brace.`);
    }

    if (!ctx.RBRACE()) {
      throw new ParseError(this.createSpan(ctx), `Expected a closing brace.`);
    }

    let args: SyntaxNode[] = [];

    let items = ctx.values();
    if (items) {
      args = (items.expression as any)().map(a => this.visit(a));
    }

    return new ListSyntax(this.createSpan(ctx), args);
  }

  visitMap(ctx: QueryExprParser.MapContext) {
    if (!ctx.LBRACE()) {
      throw new ParseError(this.createSpan(ctx), `Expected an opening brace.`);
    }

    if (!ctx.RBRACE()) {
      throw new ParseError(this.createSpan(ctx), `Expected a closing brace.`);
    }

    let args: MapSyntaxItem[] = [];

    let items = ctx.keyValues();
    if (items) {
      args = (items.keyValue as any)().map(a => this.visit(a));
    }

    return new MapSyntax(this.createSpan(ctx), args);
  }

  
  visitListExpr(ctx: QueryExprParser.ListExprContext) {
    return ctx.list().accept(this);
  }

  visitMapExpr(ctx: QueryExprParser.MapExprContext) {
    return ctx.map().accept(this);
  }


  visitKeyValue(ctx: QueryExprParser.KeyValueContext) {
    return new MapSyntaxItem( 
      ctx.key().accept(this),
      (ctx.expression() as any).accept(this)
    );
  }


  visit(ctx: any): any {
    let result = super.visit(ctx);

    if (Array.isArray(result)) {
      result = result[0];
    }

    return result;
  }

  visitChildren(ctx: any): any {
    return super.visitChildren(ctx);
  }

  visitErrorNode(ctx: any): any {
    return null;
  }

  visitTerminal(ctx: any): any {
    return null;
  }
}
